import React, { useEffect, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import "../styles/Service.css"; 

import iunidev from "../../assets/iunidev.webp";
import ayurnest from "../../assets/ayurved.webp";
import clear from "../../assets/clear_cover.webp";
import nuvex from "../../assets/nuvex.webp";
import Contact from "../Home/Contact";

// ── 1. Import Sound Controller & Audio Assets ──
import { sounds } from "../Utils/soundEffects";
import buttonClickSfx from "../../assets/sounds/tap-main.mp3";
import panelHoverSfx from "../../assets/sounds/skill-hover.mp3";

gsap.registerPlugin(ScrollTrigger);

const FRONTEND_CASE_STUDIES = {

  ecommerce: {
    title: "LifeStyle Ecommerce Website",
    subtitle: "Developed as a modern e-commerce platform for smart lifestyle products, this project highlights responsive front-end development, scalable component structure, and pixel-perfect implementation of custom UI designs.",
    ctaLink: "https://iuni-seven.vercel.app/",
    metaInfo: [
      { label: "Timeline", value: "Dec 2025" },
      { label: "Duration", value: "1 Week" },
      { label: "Stack", value: "ReactJS | CSS | HTML" }
    ],
    panels: [
      {
        heading: "WHAT NEEDED BUILDING",
        text: "Turn a detailed e-commerce UI into a responsive frontend while keeping product interactions, layouts, and visual details consistent across different screen sizes."
      },
      {
        heading: "HOW I BUILT IT",
        text: "Built the interface with reusable React components and responsive CSS, structuring layouts carefully to handle different products, screen ratios, interactions, and navigation states."
      },
      {
        heading: "WHAT CAME OUT",
        text: "A responsive and scalable storefront that stays visually close to the original design while providing smooth interactions and a consistent shopping experience across devices."
      }
    ],
    mainImage1: iunidev,
    columnRow: [
      { heading: "Responsive Development", text: "Implemented adaptive layouts using modern CSS techniques to ensure a seamless experience across desktop, tablet, and mobile devices." },
      { heading: "Interactive UI", text: "Implemented smooth hover states, product interactions, and responsive navigation to create an engaging shopping experience." },
      { heading: "Component Structure", text: "Built reusable React components for recurring interface elements, making the frontend easier to maintain, extend, and adapt as the product catalogue grows." }
    ]
  },

  ayurved: {
    title: "Ayurvedic E-commerce Website",
    subtitle: "Working on Ayurnest strengthened my frontend development skills by combining responsive UI implementation with dynamic application logic. Building reusable React components and optimizing layouts helped create an engaging digital catalog experience.",
    ctaLink: "htt",
    metaInfo: [
      { label: "Timeline", value: "March 2024" },
      { label: "Duration", value: "3 Weeks" },
      { label: "Stack", value: "ReactJS | Module CSS" }
    ],
    panels: [
      {
        heading: "WHAT NEEDED SOLVING",
        text: "Create a dynamic e-commerce experience where products, categories, filters, and shopping interactions could update smoothly without making the interface feel heavy or disconnected."
      },
      {
        heading: "HOW I BUILT IT",
        text: "Structured the application with reusable React components, centralized state management, and modular CSS to keep the interface organized as different shopping flows came together."
      },
      {
        heading: "WHAT CAME OUT",
        text: "A responsive wellness storefront with connected product and shopping flows, allowing users to browse, filter, add items, and move through the simulated checkout experience smoothly."
      }
    ],
    mainImage1: ayurnest,
    columnRow: [
      { heading: "State Management", text: "Implemented centralized state handling for dynamic product filtering, category selection, cart interactions, and efficient UI updates across the shopping experience." },
      { heading: "Reusable Components", text: "Built reusable product cards, navigation elements, cart sections, and page components to keep the application consistent and easier to extend." },
      { heading: "Shopping Flow", text: "Developed connected browsing, cart, and payment simulation flows so users can move through the core shopping journey without unnecessary interruptions." }
    ]
  },

  fintech: {
    title: "Fintech SaaS Website",
    subtitle: "This project involved designing and developing a modern fintech SaaS website for NUVEX, focused on a clean visual system, clear financial data presentation, and a polished user experience with Next.js and TypeScript.",
    ctaLink: "https://nuvex-iota.vercel.app/",
    metaInfo: [
      { label: "Timeline", value: "August 2026" },
      { label: "Duration", value: "3 Days" },
      { label: "Stack", value: "NextJS | Module CSS" }
    ],
    panels: [
      {
        heading: "WHAT NEEDED BUILDING",
        text: "Bring a visually rich fintech interface to life in code while keeping financial information clear, layouts responsive, and interactions smooth across different devices."
      },
      {
        heading: "HOW I BUILT IT",
        text: "Built the interface with Next.js and modular CSS, translating the visual system into reusable components while adding responsive layouts, transitions, and interactive states directly in the frontend."
      },
      {
        heading: "WHAT CAME OUT",
        text: "A polished SaaS frontend that combines structured financial content with modern interactions, responsive layouts, and a visual quality that carries through from the original design into the coded experience."
      }
    ],
    mainImage1: nuvex,
    columnRow: [
      { heading: "Next.js & TypeScript", text: "Built the website with Next.js and TypeScript, using structured routing, reusable components, and type-safe development for a scalable frontend." },
      { heading: "Responsive Development", text: "Developed a fully responsive interface that adapts seamlessly across desktop, tablet, and mobile while maintaining a consistent and polished experience." },
      { heading: "Interactive Experience", text: "Added smooth transitions, refined micro-interactions, and modern animations to give the interface depth and responsiveness without compromising usability." }
    ]
  },

  brand: {
    title: "Packaging Brand Website",
    subtitle: "A complete corporate website designed and developed for a sustainable bioplastic packaging company, focusing on clean presentation, modern animations, and effective storytelling for eco-friendly product lines.",
    ctaLink: "https://www.clearth.in/",
    metaInfo: [
      { label: "Timeline", value: "Aug 2025" },
      { label: "Duration", value: "2 weeks" },
      { label: "Stack", value: "HTML | CSS | ReactJS" }
    ],
    panels: [
      {
        heading: "WHAT NEEDED BUILDING",
        text: "Create a complete digital presence for a sustainable packaging brand that could communicate technical product information clearly while still feeling modern, approachable, and visually engaging."
      },
      {
        heading: "HOW I BUILT IT",
        text: "Developed the website with React, HTML, and CSS, combining reusable components with responsive layouts, visual storytelling, and scroll-based interactions to bring the brand direction into a functional frontend."
      },
      {
        heading: "WHAT CAME OUT",
        text: "A cohesive responsive website that presents the brand, products, and sustainability story through a clean interface with fluid motion and a consistent visual identity."
      }
    ],
    mainImage1: clear,
    columnRow: [
      { heading: "Responsive Frontend", text: "Developed responsive layouts and reusable components to ensure the website remains consistent and easy to navigate across different screen sizes." },
      { heading: "Scroll Interactions", text: "Implemented scroll-driven animations and transitions to create a more dynamic browsing experience while supporting the flow of the brand story." },
      { heading: "Figma to Frontend", text: "Translated the visual direction from wireframes and high-fidelity Figma designs into a functional frontend, then refined the implementation through testing and deployment." }
    ]
  }

};

export default function Front({ project, onBack }) {
  const data = FRONTEND_CASE_STUDIES[project] || FRONTEND_CASE_STUDIES.ecommerce;
  const [activePanelIdx, setActivePanelIdx] = useState(null);

  // ── 2. Preload Sounds on Mount ──
  useEffect(() => {
    sounds.loadSound("frontButtonClick", buttonClickSfx);
    sounds.loadSound("frontPanelHover", panelHoverSfx);

    const unlockAudio = () => {
      sounds.init();
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };

    window.addEventListener("pointerdown", unlockAudio);
    window.addEventListener("keydown", unlockAudio);

    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);

    let ctx = gsap.context(() => {
      gsap.fromTo(".uiux-back-btn", 
        { opacity: 0, x: -15 },
        { opacity: 1, x: 0, duration: 0.5, ease: "power2.out" }
      );

      gsap.fromTo(".case-split-header > *",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.6, ease: "power3.out", stagger: 0.12 }
      );

      gsap.fromTo(".case-panels-row .expandable-case-panel",
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, stagger: 0.08, ease: "power2.out" },
        "-=0.3"
      );

      const revealElements = document.querySelectorAll(".scroll-reveal");
      revealElements.forEach((element) => {
        const targets = element.classList.contains("uiux-metrics-row") 
          ? element.querySelectorAll(".uiux-column") 
          : element;

        gsap.fromTo(targets,
          { opacity: 0, y: 40 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: "power2.out",
            stagger: 0.15,
            scrollTrigger: {
              trigger: element,
              start: "top 85%",
              toggleActions: "play none none none"
            }
          }
        );
      });
    });

    return () => {
      ctx.revert();
      ScrollTrigger.getAll().forEach(t => t.kill());
    };
  }, [project]);

  // Click handlers with audio feedback
  const handleBackClick = () => {
    sounds.play("frontButtonClick", 0.35);
    if (onBack) onBack();
  };

  const handleCtaClick = () => {
    sounds.play("frontButtonClick", 0.35);
  };

  const handlePanelToggle = (idx, isOpen) => {
    sounds.play("frontButtonClick", 0.25);
    setActivePanelIdx(isOpen ? null : idx);
  };

  return (
    <div className="uiux-detail-wrapper frontend-detail-modifier">
      <button className="uiux-back-btn" onClick={handleBackClick} aria-label="Go Back">
        <span className="arrow">←</span>
      </button>

      {/* ── 1. Split Header ── */}
      <div className="case-split-header">
        <div className="case-title-col">
          <h1>{data.title}</h1>
        </div>

        <div className="case-desc-col">
          <p>{data.subtitle}</p>
          <a href={data.ctaLink} target="_blank" rel="noreferrer" className="uiux-project-cta" onClick={handleCtaClick}>
            Explore
          </a>
        </div>
      </div>

      {/* ── 2. Metadata Bar ── */}
      {data.metaInfo && (
        <div className="uiux-meta-bar">
          {data.metaInfo.map((item, idx) => (
            <span key={idx} className="meta-item-group">
              <strong className="meta-label">{item.label}: </strong>
              <span className="meta-value">{item.value}</span>
            </span>
          ))}
        </div>
      )}

      {/* ── 3. Upper Expandable Panels ── */}
      <div className="case-panels-row">
        {data.panels.map((panel, idx) => {
          const isOpen = activePanelIdx === idx;
          return (
            <div
              key={idx}
              className={`expandable-case-panel ${isOpen ? "is-open" : ""}`}
              onMouseEnter={() => sounds.play("frontPanelHover", 0.2)}
              onClick={() => handlePanelToggle(idx, isOpen)}
            >
              <div className="panel-header-bar">
                <span className="panel-title">{panel.heading}</span>
                <svg
                  className="panel-arrow-icon"
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="5" y1="19" x2="19" y2="5" />
                  <polyline points="7 5 19 5 19 17" />
                </svg>
              </div>
              <div className="panel-drawer">
                <div className="panel-drawer-inner">
                  <p>{panel.text}</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* ── 4. Main Media Showcase ── */}
      <div className="uiux-media-container width-80 scroll-reveal">
        <img src={data.mainImage1} alt={`${data.title} Interface Cover`} className="uiux-fluid-img" />
      </div>

      {/* ── 5. Three-Column Technical Specifications Row ── */}
      <div className="uiux-metrics-row scroll-reveal">
        {data.columnRow.map((col, index) => (
          <div className="uiux-column" key={index}>
            <h2>{col.heading}</h2>
            <p>{col.text}</p>
          </div>
        ))}
      </div>

      <Contact />
    </div>
  );
}